import React, { useState, useEffect, useRef } from "react";
import logo from "../../Assets/logo.png";
import footerLogo from "../../Assets/footerlogo.png";
import cert1 from "../../Assets/Mrs. Neeraj Gaur.pdf";
import cert2 from "../../Assets/Ms. Neeraj Gaur vedic maths franchise.pdf";
import cert3 from "../../Assets/Ms. Neeraj Gaur.pdf";
import cert4 from "../../Assets/upgrad certificate ng.pdf";
import trainerImage from "../../Assets/ngsaree.jpg";





// A simple hook to check if an element is visible in the viewport
const useInView = (ref, options) => {
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, options);

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, options]);
  return isInView;
};

// Animated Counter Component for stats
const Counter = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const endValue = parseInt(end);
      if (start === endValue) return;

      let totalFrames = duration * 60; // Assumes 60fps
      let increment = endValue / totalFrames;

      const counter = () => {
        start += increment;
        if (start < endValue) {
          setCount(Math.ceil(start));
          requestAnimationFrame(counter);
        } else {
          setCount(endValue);
        }
      };
      requestAnimationFrame(counter);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}</span>;
};

// Floating background graphics for a playful feel
const BackgroundGraphics = () => {
  const symbols = ["+", "−", "×", "÷", "√", "π", "∑", "∫", "∞", "="];
  return (
    <div className="background-graphics">
      {symbols.map((s, i) => (
        <span
          key={i}
          style={{
            "--i": Math.random() * 20,
            "--d": Math.random() * 10 + 10, // duration
            "--s": Math.random() * 10 + 5, // size
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * -20}s`,
          }}
        >
          {s}
        </span>
      ))}
    </div>
  );
};

// FAQ Component for better organization
const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      q: "What is the right age to start Vedic Maths?",
      a: "The ideal age is around 10 years (Class 5). At this stage, children have a good grasp of basic arithmetic, and Vedic Maths can build upon that foundation effectively, making them faster and more confident.",
    },
    {
      q: "Will this conflict with the school curriculum?",
      a: "Not at all. Vedic Maths complements the school curriculum. It provides faster, alternative methods for calculations and helps in verifying answers, which is a great advantage during exams.",
    },
    {
      q: "Are the classes online or offline?",
      a: "We offer both online and offline classes to cater to different needs. Our online classes are live, interactive sessions, not pre-recorded videos, ensuring personal attention for every student.",
    },
    {
      q: "How quickly can we see results?",
      a: "Parents and students often notice a significant improvement in calculation speed, accuracy, and confidence within the first few weeks of starting the foundation course.",
    },
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="faq-container">
      {faqs.map((faq, index) => (
        <div
          className={`faq-item ${activeIndex === index ? "active" : ""}`}
          key={index}
        >
          <button className="faq-question" onClick={() => toggleFAQ(index)}>
            {faq.q}
            <span className="faq-icon">+</span>
          </button>
          <div className="faq-answer">
            <p>{faq.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Main App Component
const App = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Smooth scroll function
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80; // Height of the fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveSection(id);
      setIsMenuOpen(false); // Close mobile menu after clicking
    }
  };

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Intersection Observer for scroll animations and active nav link
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            entry.target.classList.add("is-visible");
          }
        });
      },
      {
        root: null,
        rootMargin: "-40% 0px -60% 0px", // Activates when section is in the middle 20% of the screen
        threshold: 0,
      }
    );

    const currentRefs = sectionRefs.current;
    Object.values(currentRefs).forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      Object.values(currentRefs).forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  return (
    <>
      <style>
        {`
        /* --- FONT IMPORT & CSS VARIABLES --- */
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@400;600;700;800&display=swap');

        :root {
          --primary-color: #0d6efd; /* Professional Blue */
          --secondary-color: #ffc107; /* Sunny Yellow */
          --accent-color: #ff5722; /* Fun Orange */
          --dark-color: #333;
          --light-color: #f8f9fa;
          --bg-light-alt: #e9ecef;
          --text-color: #444;
          --text-light: #666;
          --white: #ffffff;
          --header-height: 80px;
          --border-radius: 20px;
          --shadow-sm: 0 4px 10px rgba(0, 0, 0, 0.08);
          --shadow-md: 0 8px 25px rgba(0, 0, 0, 0.12);
          --font-primary: 'Fredoka One', cursive; /* Fun, rounded font for headers */
          --font-secondary: 'Nunito', sans-serif; /* Clear, friendly font for body */
        }

        /* --- BASE STYLES --- */
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          font-family: var(--font-secondary);
          background-color: var(--white);
          color: var(--text-color);
          line-height: 1.7;
          font-size: 17px;
          scroll-behavior: smooth;
          overflow-x: hidden;
        }

        img {
          max-width: 100%;
          height: auto;
          display: block;
        }

        .container {
          width: 100%;
          max-width: 1140px;
          margin-left: auto;
          margin-right: auto;
          padding-left: 1rem;
          padding-right: 1rem;
        }

        h1, h2, h3, h4, h5, h6 {
          font-family: var(--font-primary);
          font-weight: 400;
          color: var(--primary-color);
          line-height: 1.3;
          letter-spacing: 1px;
          margin-top: 0;
        }
        
        p {
          max-width: 75ch;
          margin-left: auto;
          margin-right: auto;
        }
        
        /* --- BACKGROUND GRAPHICS --- */
        @keyframes float {
            0% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
            50% { transform: translateY(-25px) rotate(180deg); opacity: 1; }
            100% { transform: translateY(0) rotate(360deg); opacity: 0.5; }
        }

        .background-graphics {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            overflow: hidden;
        }

        .background-graphics span {
            position: absolute;
            color: var(--secondary-color);
            font-size: calc(15px + var(--s) * 1px);
            opacity: 0;
            animation: float calc(2s * var(--d)) linear infinite;
            animation-delay: calc(-0.1s * var(--i));
        }
        @media (max-width: 768px) {
            .background-graphics { display: none; }
        }

        /* --- GLOBAL & ANIMATION --- */
        .content-section {
          padding: 80px 0;
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .content-section.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .section-header {
            text-align: center;
            margin-bottom: 60px;
        }

        .section-title {
          font-size: 3.5rem;
          margin-bottom: 0.5rem;
          position: relative;
          display: inline-block;
          color: var(--primary-color);
        }
        
        .section-title::after {
            content: '✨';
            position: absolute;
            bottom: -25px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 2rem;
            color: var(--secondary-color);
        }

        .section-subtitle {
            font-size: 1.2rem;
            color: var(--text-light);
            max-width: 700px;
            margin: 1.5rem auto 0 auto;
            font-weight: 600;
        }

        .btn {
            padding: 16px 35px;
            border-radius: 50px;
            font-weight: 800;
            font-family: var(--font-secondary);
            text-decoration: none;
            display: inline-block;
            transition: all 0.3s ease;
            border: 3px solid var(--dark-color);
            cursor: pointer;
            font-size: 1.1rem;
            box-shadow: 5px 5px 0px var(--dark-color);
            min-width: 44px;
            min-height: 44px;
            text-align: center;
        }
        .btn:hover {
            transform: translateY(-2px) translateX(-2px);
            box-shadow: 7px 7px 0px var(--dark-color);
        }
        .btn:active {
            transform: translateY(2px) translateX(2px);
            box-shadow: 3px 3px 0px var(--dark-color);
        }

        .btn-primary {
            background-color: var(--primary-color);
            color: var(--white);
        }

        .btn-secondary {
            background-color: var(--secondary-color);
            color: var(--dark-color);
        }

        /* --- HEADER & NAVBAR --- */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          background-color: rgba(255, 255, 255, 0.9);
          box-shadow: var(--shadow-sm);
          z-index: 1000;
          backdrop-filter: blur(8px);
        }

        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: var(--header-height);
        }

        .logo-text {
          font-family: var(--font-primary);
          font-size: 2.2rem;
          color: var(--primary-color);
          cursor: pointer;
          margin: 0;
          display: flex;
          align-items: center;
        }

        .logo-img {
          height: 50px;
          width: auto;
          object-fit: contain;
        }

        
        .menu-button {
          display: none;
          padding: 0.5rem;
          border: none;
          background: transparent;
          cursor: pointer;
          z-index: 1001;
          min-width: 44px;
          min-height: 44px;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 2.5rem;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-link {
          font-size: 1.1rem;
          font-weight: 700;
          font-family: var(--font-secondary);
          color: var(--text-color);
          text-decoration: none;
          padding-bottom: 5px;
          position: relative;
          transition: color 0.3s ease;
          min-width: 44px;
          min-height: 44px;
          display: flex;
          align-items: center;
        }
        
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 4px;
          background-color: var(--secondary-color);
          transition: width 0.3s ease;
          border-radius: 2px;
        }

        .nav-link:hover, .nav-link.active {
          color: var(--primary-color);
        }
        
        .nav-link.active::after {
          width: 100%;
        }

        .nav-actions {
          display: flex;
        }

        @media (max-width: 992px) {
            .nav-links {
                display: none;
                flex-direction: column;
                position: absolute;
                top: var(--header-height);
                left: 0;
                width: 100%;
                background-color: var(--white);
                box-shadow: 0 10px 15px rgba(0,0,0,0.1);
                padding: 1.5rem 0;
                gap: 1.8rem;
            }
            .nav-links.open {
                display: flex;
            }
            .menu-button {
                display: block;
            }
        }
        
        /* --- MAIN CONTENT --- */
        .main-content {
          padding-top: var(--header-height);
          position: relative;
          z-index: 10;
          background-color: var(--white);
        }
        
        #benefits, #about, #faq, #gallery {
            background-color: var(--light-color);
        }
        #testimonials, #contact, #programs {
            background-color: var(--bg-light-alt);
        }

        /* --- HERO SECTION --- */
        .hero-section {
          height: calc(100vh - var(--header-height));
          min-height: 650px;
          display: flex;
          align-items: center;
          background: linear-gradient(rgba(13, 110, 253, 0.7), rgba(13, 110, 253, 0.7)), url('https://placehold.co/1920x1080/e3f2fd/333333?text=Fear-Free+Maths') no-repeat center center/cover;
          color: var(--white);
          position: relative;
        }

        .hero-content {
          max-width: 800px;
          text-align: center;
          margin: 0 auto;
          animation: fadeInHero 1s 0.5s ease-out forwards;
          opacity: 0;
          transform: translateY(20px);
        }
        
        @keyframes fadeInHero {
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        .hero-subtitle {
          font-size: 1.5rem;
          font-weight: 700;
          font-family: var(--font-secondary);
          color: var(--secondary-color);
          letter-spacing: 1px;
        }

        .hero-title {
          font-size: 5.5rem;
          line-height: 1.1;
          margin: 15px 0 25px 0;
          color: var(--white);
          text-shadow: 4px 4px 0px rgba(0,0,0,0.2);
        }

        .hero-description {
          font-size: 1.3rem;
          margin-bottom: 40px;
          max-width: 650px;
          margin-left: auto;
          margin-right: auto;
          color: rgba(255,255,255,0.95);
          font-weight: 600;
        }
        
        /* --- ABOUT VEDIC MATHS --- */
        .about-vedic-maths-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }
        .about-vedic-maths-image {
          width: 100%;
          border-radius: var(--border-radius);
          border: 4px solid var(--dark-color);
          transition: transform 0.5s ease, box-shadow 0.5s ease;
        }
        .about-vedic-maths-image:hover {
            transform: rotate(3deg);
        }
        .about-vedic-maths-content .section-header { text-align: left; margin-bottom: 2rem; }
        .about-vedic-maths-content .section-title, .about-vedic-maths-content .section-subtitle { margin-left: 0; margin-right: 0; }
        .about-vedic-maths-content .section-title::after { left: 0; transform: translateX(0); }
        .about-text {
            margin-bottom: 1.5rem;
            color: var(--text-light);
            font-size: 1.1rem;
        }
        .sutras-highlight {
            background-color: #fff9c4;
            border-left: 6px solid var(--secondary-color);
            padding: 1.5rem;
            margin: 1.5rem 0;
            border-radius: 0 var(--border-radius) var(--border-radius) 0;
            font-weight: 700;
        }

        /* --- BENEFITS SECTION --- */
        .benefits-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2.5rem;
            margin-top: 50px;
        }
        .benefit-card {
            background: var(--white);
            padding: 2.5rem 2rem;
            text-align: center;
            border-radius: var(--border-radius);
            border: 3px solid var(--dark-color);
            box-shadow: 6px 6px 0 var(--primary-color);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            opacity: 0;
            transform: translateY(30px);
        }
        .is-visible .benefit-card {
            opacity: 1;
            transform: translateY(0);
            transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .is-visible .benefit-card:nth-child(1) { transition-delay: 0.1s; }
        .is-visible .benefit-card:nth-child(2) { transition-delay: 0.2s; }
        .is-visible .benefit-card:nth-child(3) { transition-delay: 0.3s; }
        .is-visible .benefit-card:nth-child(4) { transition-delay: 0.4s; }
        .is-visible .benefit-card:nth-child(5) { transition-delay: 0.5s; }
        .is-visible .benefit-card:nth-child(6) { transition-delay: 0.6s; }
        
        .benefit-card:hover {
            transform: translateY(-5px) translateX(-5px);
            box-shadow: 10px 10px 0 var(--accent-color);
        }
        .benefit-icon {
            font-size: 4rem;
            margin-bottom: 1.5rem;
            line-height: 1;
        }
        .benefit-title {
            font-size: 1.8rem;
            color: var(--dark-color);
            margin-bottom: 0.7rem;
        }

        /* --- PROGRAMS (PRICING) SECTION --- */
        .programs-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 2.5rem;
            margin-top: 50px;
            align-items: stretch;
        }
        
        .program-card {
            background: var(--white);
            border-radius: var(--border-radius);
            border: 3px solid var(--dark-color);
            padding: 2.5rem;
            text-align: center;
            transition: all 0.3s ease;
            display: flex;
            flex-direction: column;
        }
        
        .program-card.featured {
            transform: scale(1.05);
            border-color: var(--secondary-color);
            box-shadow: 0 0 20px rgba(255, 193, 7, 0.5);
        }

        .program-card-header h3 {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        .program-card-header p {
            font-size: 1.1rem;
            color: var(--text-light);
            min-height: 40px;
            font-weight: 600;
        }
        .price {
            font-size: 4rem;
            font-weight: 400;
            font-family: var(--font-primary);
            color: var(--dark-color);
            margin: 1.5rem 0;
        }
        .price span {
            font-size: 1rem;
            font-weight: 600;
            color: var(--text-light);
        }
        .features-list {
            list-style: none;
            padding: 0;
            margin: 2rem 0;
            text-align: left;
            flex-grow: 1;
            font-weight: 600;
        }
        .features-list li {
            margin-bottom: 1rem;
            display: flex;
            align-items: center;
        }
        .features-list .icon {
            font-size: 1.5rem;
            margin-right: 0.8rem;
            color: var(--primary-color);
        }

        /* --- GALLERY SECTION --- */
        .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
        }
        .gallery-item {
            border-radius: var(--border-radius);
            overflow: hidden;
            border: 3px solid var(--dark-color);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .gallery-item:hover {
            transform: scale(1.05) rotate(2deg);
            box-shadow: var(--shadow-md);
        }
        .gallery-image {
            width: 100%;
            height: 250px;
            object-fit: cover;
            display: block;
        }
        
        /* --- TESTIMONIALS SECTION --- */
        .testimonials-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 2rem;
        }
        .testimonial-card {
            background: var(--white);
            padding: 2rem;
            border-radius: var(--border-radius);
            border: 3px solid var(--dark-color);
            position: relative;
        }
        .testimonial-card::before {
            content: '💬';
            position: absolute;
            top: 10px;
            left: 15px;
            font-size: 3rem;
            opacity: 0.8;
        }
        .testimonial-text {
            font-style: italic;
            color: var(--text-light);
            margin-bottom: 1.5rem;
            font-size: 1.1rem;
            padding-top: 2rem;
        }
        .testimonial-author {
            font-weight: 800;
            font-family: var(--font-secondary);
            color: var(--dark-color);
        }
        .testimonial-author span {
            font-weight: 600;
            color: var(--text-light);
            font-size: 0.9rem;
        }

        /* --- ABOUT TRAINER SECTION --- */
        .about-trainer-grid {
          display: grid;
          grid-template-columns: 400px 1fr;
          gap: 4rem;
          align-items: center;
        }
        .about-trainer-image-wrapper {
            width: 100%;
            max-width: 400px;
            height: 500px;
            border-radius: var(--border-radius);
            border: 4px solid var(--dark-color);
            overflow: hidden;
            margin: 0 auto;
        }
        .about-trainer-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .about-trainer-image-wrapper:hover .about-trainer-image {
            transform: scale(1.1);
        }
        .about-trainer-content .section-header { text-align: left; margin-bottom: 2rem; }
        .about-trainer-content .section-title, .about-trainer-content .section-subtitle { margin-left: 0; margin-right: 0; }
        .about-trainer-content .section-title::after { left: 0; transform: translateX(0); }

        /* --- FAQ SECTION --- */
        .faq-container {
            max-width: 800px;
            margin: 50px auto 0 auto;
        }
        .faq-item {
            background: var(--white);
            border: 3px solid #ddd;
            border-radius: var(--border-radius);
            margin-bottom: 1rem;
            overflow: hidden;
        }
        .faq-question {
            width: 100%;
            background: transparent;
            border: none;
            text-align: left;
            padding: 1.5rem;
            font-size: 1.5rem;
            font-family: var(--font-primary);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: var(--dark-color);
            min-width: 44px;
            min-height: 44px;
        }
        .faq-answer {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.5s ease-in-out, padding 0.5s ease;
            padding: 0 1.5rem;
        }
        .faq-answer p {
            margin: 0;
            padding-bottom: 1.5rem;
            color: var(--text-light);
            font-weight: 600;
        }
        .faq-item.active .faq-answer {
            max-height: 300px;
        }
        .faq-icon {
            transition: transform 0.3s ease;
            font-size: 2rem;
            color: var(--accent-color);
        }
        .faq-item.active .faq-icon {
            transform: rotate(45deg);
        }
        
        /* --- CONTACT SECTION --- */
        .contact-grid {
            display: grid;
            grid-template-columns: 1fr 1.5fr;
            gap: 3rem;
            align-items: center;
        }
        .contact-info-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .contact-info-list li {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1.5rem;
            font-size: 1.1rem;
        }
        .contact-info-list .icon {
            font-size: 2.5rem;
            color: var(--primary-color);
            margin-right: 1.5rem;
            width: 30px;
            text-align: center;
        }
        .contact-info-list a {
            color: var(--text-color);
            text-decoration: none;
            transition: color 0.3s ease;
            font-weight: 700;
        }
        .contact-info-list a:hover {
            color: var(--primary-color);
        }
        .contact-form-wrapper {
            background: var(--white);
            padding: 2rem;
            border-radius: var(--border-radius);
            border: 3px solid var(--dark-color);
        }
        .contact-form {
            width: 100%;
            height: 550px;
            border: none;
            border-radius: calc(var(--border-radius) - 5px);
        }

        /* --- CERTIFICATIONS SECTION --- */
        .certifications-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 2rem;
            margin-top: 3rem;
        }

        .cert-card {
            background: var(--white);
            padding: 2rem;
            border-radius: var(--border-radius);
            border: 3px solid var(--dark-color);
            text-align: center;
            transition: all 0.3s ease;
            cursor: pointer;
            text-decoration: none;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
        }

        .cert-card:hover {
            transform: translateY(-10px);
            box-shadow: var(--shadow-md);
            border-color: var(--secondary-color);
        }

        .cert-icon {
            font-size: 3.5rem;
            margin-bottom: 1rem;
        }

        .cert-title {
            font-size: 1.1rem;
            font-weight: 800;
            color: var(--dark-color);
            font-family: var(--font-secondary);
        }



        /* --- FOOTER --- */
        .footer {
            background-color: var(--dark-color);
            color: #ccc;
            padding: 80px 0 30px 0;
            position: relative;
            z-index: 10;
        }
        .footer-grid {
            display: grid;
            grid-template-columns: 2fr 1fr 1fr 1.5fr;
            gap: 3rem;
            margin-bottom: 50px;
        }
        .footer-col h4 {
            color: var(--white);
            font-size: 1.5rem;
            margin-bottom: 1.5rem;
        }
        .footer-col p, .footer-col a {
            color: #ccc;
            text-decoration: none;
            margin-bottom: 0.8rem;
            transition: color 0.3s ease;
            display: block;
            font-weight: 600;
        }
        .footer-col a:hover {
            color: var(--secondary-color);
        }
        .footer-links {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .footer-logo {
            font-family: var(--font-primary);
            font-size: 2.5rem;
            color: var(--white);
            margin-bottom: 1rem;
            display: inline-block;
        }

        .footer-logo img {
            height: 60px;
            width: auto;
            object-fit: contain;
        }

        .footer-bottom {
            text-align: center;
            padding-top: 30px;
            border-top: 1px solid #444;
            font-size: 0.9rem;
            color: #aaa;
        }

        /* ====================== */
        /* RESPONSIVE ADJUSTMENTS */
        /* ====================== */

        /* Extra Small Devices (350px - 576px) */
        @media (max-width: 576px) {
          /* Header adjustments */
          .logo-text {
            font-size: 1.8rem;
          }
          
          .menu-button {
            padding: 0.25rem;
          }
          
          /* Hero section */
          .hero-title {
            font-size: 2.5rem;
            line-height: 1.2;
            margin: 10px 0 15px 0;
          }
          
          .hero-subtitle {
            font-size: 1.1rem;
          }
          
          .hero-description {
            font-size: 1rem;
            margin-bottom: 30px;
          }
          
          .btn {
            padding: 12px 25px;
            font-size: 1rem;
          }
          
          /* Section titles */
          .section-title {
            font-size: 2.2rem;
          }
          
          .section-subtitle {
            font-size: 1rem;
          }
          
          /* About section */
          .about-trainer-image-wrapper {
            height: 350px;
          }
          
          /* Programs section */
          .program-card {
            padding: 1.5rem;
          }
          
          .price {
            font-size: 3rem;
          }
          
          /* Contact section */
          .contact-form-wrapper {
            padding: 1rem;
          }
          
          .contact-form {
            height: 450px;
          }
          
          /* Footer */
          .footer-col {
            margin-bottom: 2rem;
          }
        }

        /* Small Devices (577px - 768px) */
        @media (min-width: 577px) and (max-width: 768px) {
          .hero-title {
            font-size: 3rem;
          }
          
          .section-title {
            font-size: 2.8rem;
          }
          
          .about-trainer-grid,
          .about-vedic-maths-grid,
          .contact-grid {
            gap: 2rem;
          }
        }

        /* Medium Devices (769px - 992px) */
        @media (min-width: 769px) and (max-width: 992px) {
          .hero-title {
            font-size: 4rem;
          }
          
          .section-title {
            font-size: 3rem;
          }
          
          .about-trainer-content,
          .about-vedic-maths-content {
            padding: 0 1rem;
          }
        }

        /* Large Devices (1200px - 1800px) */
        @media (min-width: 1200px) {
          .container {
            max-width: 1320px;
          }
        }

        /* Extra Large Devices (1800px and up) */
        @media (min-width: 1800px) {
          .container {
            max-width: 1600px;
          }
          
          .content-section {
            padding: 120px 0;
          }
          
          .section-title {
            font-size: 4rem;
          }
        }

        /* General Mobile Improvements */
        @media (max-width: 768px) {
          /* Navigation */
          .nav-links.open {
            padding: 1rem 0;
            gap: 1.2rem;
          }
          
          .nav-link {
            font-size: 1rem;
          }
          
          /* Content sections */
          .content-section {
            padding: 60px 0;
          }
          
          /* Benefits grid */
          .benefits-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          /* Testimonials */
          .testimonials-grid {
            grid-template-columns: 1fr;
          }
          
          /* FAQ */
          .faq-question {
            font-size: 1.2rem;
            padding: 1rem;
          }

          /* Grid layouts */
          .about-trainer-grid,
          .about-vedic-maths-grid,
          .contact-grid {
            grid-template-columns: 1fr;
          }

          .programs-grid {
            grid-template-columns: 1fr;
          }

          .footer-grid {
            grid-template-columns: 1fr;
            text-align: center;
          }
        }

        /* Tablet Landscape Improvements */
        @media (min-width: 768px) and (max-width: 992px) and (orientation: landscape) {
          .hero-section {
            min-height: 500px;
          }
          
          .about-trainer-image-wrapper {
            height: 400px;
          }
        }

        /* Adjustments for medium screens */
        @media (max-width: 992px) {
          .program-card.featured {
            transform: scale(1);
          }

          .about-trainer-content .section-header,
          .about-vedic-maths-content .section-header { 
            text-align: center; 
          }

          .about-trainer-content .section-title::after,
          .about-vedic-maths-content .section-title::after { 
            left: 50%; 
            transform: translateX(-50%); 
          }
        }
        `}
      </style>
      <div className="vedic-maths-app">
        <BackgroundGraphics />
        {/* Header/Navigation */}
        <header className="header">
          <nav className="navbar container">
            <div
              className="logo-text"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <img src={logo} alt="Neeraj Gaur" className="logo-img" />
            </div>


            <button
              className="menu-button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation"
            >
              {isMenuOpen ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>

            <ul className={`nav-links ${isMenuOpen ? "open" : ""}`}>
              {[
                { id: "home", title: "Home" },
                { id: "about", title: "About Me" },
                { id: "certifications", title: "Certifications" },
                { id: "programs", title: "Programs" },
                { id: "benefits", title: "Benefits" },
                { id: "testimonials", title: "Testimonials" },
                { id: "faq", title: "FAQ" },
              ].map((item) => (

                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.id);
                    }}
                    className={`nav-link ${activeSection === item.id ? "active" : ""
                      }`}
                  >
                    {item.title}
                  </a>
                </li>
              ))}
              <li className="nav-actions">
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("contact");
                  }}
                  className="btn btn-secondary"
                >
                  Contact Me
                </a>
              </li>
            </ul>
          </nav>
        </header>

        {/* Main Content Sections */}
        <main className="main-content">
          {/* Hero Section */}
          <section
            id="home"
            className="hero-section"
            ref={(el) => (sectionRefs.current.home = el)}
          >
            <div className="container">
              <div className="hero-content">
                <p className="hero-subtitle">
                  EMPOWERING CHILDREN WITH CONFIDENCE IN MATHS
                </p>
                <h1 className="hero-title">Make Maths Fast, Fun, and Easy!</h1>
                <p className="hero-description">
                  Join me on a mission to remove the fear of mathematics and
                  help your child build confidence, speed, and a genuine love
                  for learning through the power of Vedic Maths.
                </p>
                <button
                  onClick={() => scrollToSection("programs")}
                  className="btn btn-primary"
                >
                  Explore My Programs
                </button>
              </div>
            </div>
          </section>

          {/* About Me Section */}
          <section
            id="about"
            className="content-section"
            ref={(el) => (sectionRefs.current.about = el)}
          >
            <div className="container">
              <div className="about-trainer-grid">
                <div className="about-trainer-image-wrapper">
                  <img
                    src={trainerImage}
                    alt="Neeraj Gaur, Vedic Maths Teacher"

                    className="about-trainer-image"
                  />
                </div>
                <div className="about-trainer-content">
                  <div className="section-header">
                    <h2 className="section-title">About Me</h2>
                    <p className="section-subtitle">
                      Neeraj Gaur - Educator & Maths Mentor
                    </p>
                  </div>
                  <p className="about-text">
                    I am a passionate educator with over{" "}
                    <strong>20+ years of experience</strong> in Computer
                    Hardware and Software as a Trainer, Faculty, and Support
                    Engineer. After two successful decades in the tech training
                    industry, I have now embarked on a heartfelt mission.
                  </p>
                  <p className="about-text">
                    My goal is to help children across India overcome their{" "}
                    <strong>fear of mathematics</strong> and develop a love for
                    learning through the power of Vedic Maths. I believe every
                    child can be good at maths with the right approach and
                    support.
                  </p>
                  <p className="about-text">
                    I am a certified trainer by the{" "}
                    <strong>Vedic Maths Global Federation</strong>, dedicated to
                    making "Fear-Free Maths" a reality for every child!
                  </p>

                  <button
                    onClick={() => scrollToSection("contact")}
                    className="btn btn-primary"
                  >
                    Get In Touch
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Certifications Section */}
          <section
            id="certifications"
            className="content-section"
            ref={(el) => (sectionRefs.current.certifications = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">My Certifications</h2>
                <p className="section-subtitle">
                  Recognized expertise and professional qualifications in Vedic
                  Mathematics and beyond.
                </p>
              </div>
              <div className="certifications-grid">
                {[
                  {
                    title: "Vedic Maths Certification",
                    file: cert1,
                    icon: "📜",
                  },
                  {
                    title: "Vedic Maths Franchise",
                    file: cert2,
                    icon: "🏢",
                  },
                  {
                    title: "Expert Educator Certificate",
                    file: cert3,
                    icon: "🎓",
                  },
                  {
                    title: "Upgrad Professional Certificate",
                    file: cert4,
                    icon: "⭐",
                  },
                ].map((cert, index) => (
                  <a
                    key={index}
                    href={cert.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cert-card"
                  >
                    <div className="cert-icon">{cert.icon}</div>
                    <h3 className="cert-title">{cert.title}</h3>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        marginTop: "10px",
                        color: "var(--primary-color)",
                        fontWeight: "bold",
                      }}
                    >
                      View Certificate →
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </section>


          {/* Programs Section */}
          <section
            id="programs"
            className="content-section"
            ref={(el) => (sectionRefs.current.programs = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">What I Offer</h2>
                <p className="section-subtitle">
                  Fun, engaging sessions designed to build a strong mathematical
                  foundation and boost confidence.
                </p>
              </div>
              <div className="programs-grid">
                <div className="program-card">
                  <div className="program-card-header">
                    <h3>Foundation Course</h3>
                    <p>For students in Classes 5th - 8th</p>
                  </div>
                  <ul className="features-list">
                    <li>
                      <span className="icon">✔️</span> Build a strong
                      mathematical base
                    </li>
                    <li>
                      <span className="icon">✔️</span> Improve calculation speed
                      & accuracy
                    </li>
                    <li>
                      <span className="icon">✔️</span> Eliminate math phobia
                    </li>
                    <li>
                      <span className="icon">✔️</span> Fun and engaging live
                      sessions
                    </li>
                  </ul>
                  <button className="btn btn-primary">Learn More</button>
                </div>
                <div className="program-card featured">
                  <div className="program-card-header">
                    <h3>Advanced Course</h3>
                    <p>For students in Classes 9th - 12th</p>
                  </div>
                  <ul className="features-list">
                    <li>
                      <span className="icon">🚀</span> Tackle complex problems
                      with ease
                    </li>
                    <li>
                      <span className="icon">🚀</span> Prepare for competitive
                      exams
                    </li>
                    <li>
                      <span className="icon">🚀</span> Verify board exam answers
                      quickly
                    </li>
                    <li>
                      <span className="icon">🚀</span> Master advanced Vedic
                      techniques
                    </li>
                  </ul>
                  <button className="btn btn-secondary">Enroll Now</button>
                </div>
                <div className="program-card">
                  <div className="program-card-header">
                    <h3>School Workshops</h3>
                    <p>Engaging sessions for schools</p>
                  </div>
                  <ul className="features-list">
                    <li>
                      <span className="icon">🏫</span> Interactive workshops for
                      groups
                    </li>
                    <li>
                      <span className="icon">🏫</span> Make mathematics fun for
                      the class
                    </li>
                    <li>
                      <span className="icon">🏫</span> Customized content for
                      school needs
                    </li>
                    <li>
                      <span className="icon">🏫</span> Partner with us to
                      inspire students
                    </li>
                  </ul>
                  <button className="btn btn-primary">Inquire Now</button>
                </div>
              </div>
            </div>
          </section>
          <section
            id="about-vedic"
            className="content-section container"
            ref={(el) => (sectionRefs.current["about-vedic"] = el)}
          >
            {" "}
            <div className="about-vedic-maths-grid">
              <div className="about-vedic-maths-image-wrapper">
                <img
                  src="https://assets.cdn.filesafe.space/URDkr9fLfoLOLg0aD0Yw/media/4b83b856-9ea0-42d1-bc55-01e4a1f0368c.jpeg"
                  alt="Vedic Mathematics Concept"
                  className="about-vedic-maths-image"
                />
              </div>
              <div className="about-vedic-maths-content">
                <div className="section-header">
                  <h2 className="section-title">What's Vedic Maths?</h2>
                  <p className="section-subtitle">
                    It's like a secret code for numbers!
                  </p>
                </div>
                <p className="about-text">
                  A long, long time ago in India, wise people discovered super
                  cool shortcuts for math. These shortcuts are called Sutras,
                  and they make big, tricky problems super easy and fun to solve
                  in your head!
                </p>
                <div className="sutras-highlight">
                  <p>
                    There are 16 secret formulas that turn you into a human
                    calculator. Shhh, it's our secret!
                  </p>
                </div>
                <p className="about-text">
                  Instead of being scared of numbers, you'll learn to play with
                  them. It's all about making your brain sharper, faster, and
                  full of awesome ideas!
                </p>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="btn btn-primary"
                >
                  Learn the Secrets!
                </button>
              </div>
            </div>
          </section>

          <section
            id="benefits"
            className="content-section"
            ref={(el) => (sectionRefs.current.benefits = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Why Vedic Maths?</h2>
                <p className="section-subtitle">
                  Unlock powerful mental calculation techniques that are simple,
                  effective, and transformative.
                </p>
              </div>
              <div className="benefits-grid">
                {[
                  {
                    icon: "🚀",
                    title: (
                      <>
                        Calculations <Counter end={10} />x Faster
                      </>
                    ),
                    text: "Solve problems in seconds, not minutes, boosting efficiency.",
                  },
                  {
                    icon: "🧠",
                    title: "Boosts Brainpower",
                    text: "Sharpens the mind, improves memory, and enhances concentration.",
                  },
                  {
                    icon: "😎",
                    title: "Removes Math Fear",
                    text: "Builds confidence and turns anxiety into a positive attitude towards math.",
                  },
                  {
                    icon: "✅",
                    title: "Improves Accuracy",
                    text: "Unique checking methods ensure answers are correct, reducing errors.",
                  },
                  {
                    icon: "🏆",
                    title: "Better Exam Scores",
                    text: "Saves valuable time in exams, leading to improved performance.",
                  },
                  {
                    icon: "🎨",
                    title: "Creative Problem Solving",
                    text: "Encourages logical thinking and alternative approaches to problems.",
                  },
                ].map((item, index) => (
                  <div className="benefit-card" key={index}>
                    <div className="benefit-icon">{item.icon}</div>
                    <h3 className="benefit-title">{item.title}</h3>
                    <p className="benefit-text">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section
            id="testimonials"
            className="content-section"
            ref={(el) => (sectionRefs.current.testimonials = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Why Parents Trust Me</h2>
                <p className="section-subtitle">
                  Real feedback from parents who have witnessed the
                  transformation in their children.
                </p>
              </div>
              <div className="testimonials-grid">
                <div className="testimonial-card">
                  <p className="testimonial-text">
                    "My son's fear of math is completely gone. He now enjoys
                    solving problems and his grades have improved significantly.
                    Thank you, Neeraj sir!"
                  </p>
                  <p className="testimonial-author">
                    - Sunita S. <span>(Proud Parent)</span>
                  </p>
                </div>
                <div className="testimonial-card">
                  <p className="testimonial-text">
                    "The speed and accuracy my daughter has gained are just
                    amazing. Vedic Maths has been a game-changer for her
                    competitive exam preparations. Highly recommended!"
                  </p>
                  <p className="testimonial-author">
                    - Ramesh K. <span>(Happy Parent)</span>
                  </p>
                </div>
                <div className="testimonial-card">
                  <p className="testimonial-text">
                    "I was skeptical at first, but seeing my child solve complex
                    sums mentally is magical. The online classes are very
                    interactive and personal attention is given to each
                    student."
                  </p>
                  <p className="testimonial-author">
                    - Priya P. <span>(Amazed Parent)</span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section
            id="faq"
            className="content-section"
            ref={(el) => (sectionRefs.current.faq = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <p className="section-subtitle">
                  Have questions? I've got answers. Here are some of the most
                  common queries.
                </p>
              </div>
              <FAQ />
            </div>
          </section>

          {/* Contact Section */}
          <section
            id="contact"
            className="content-section"
            ref={(el) => (sectionRefs.current.contact = el)}
          >
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">Book a Free Demo!</h2>
                <p className="section-subtitle">
                  Take the first step towards a maths-positive future! Fill out
                  the form to schedule a free, no-obligation demo class.
                </p>
              </div>
              <div className="contact-grid">
                <div className="contact-info">
                  <h4>Get in Touch</h4>
                  <p>
                    If you are a parent, student, or school interested in making
                    maths easier and more enjoyable, I invite you to connect
                    with me.
                  </p>
                  <ul className="contact-info-list">
                    <li>
                      <span className="icon">📞</span>
                      <div>
                        <strong>Phone</strong>
                        <br />
                        <a href="tel:+919876543210">+91 9599316407</a>
                      </div>
                    </li>
                    <li>
                      <span className="icon">✉️</span>
                      <div>
                        <strong>Email</strong>
                        <br />
                        <a href="mailto:neerugaur8107@gmail.com">
                          neerugaur8107@gmail.com
                        </a>
                      </div>
                    </li>
                    <li>
                      <span className="icon">📍</span>
                      <div>
                        <strong>Location</strong>
                        <br />
                        <span>
                          New Delhi, India (Online classes available
                          everywhere!)
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="contact-form-wrapper">
                  <iframe
                    src="https://app.autopilotmybusiness.com/forms/view/6a016007c746c2c55999c352?c=c2hyZWUtaGFyaS10ZWNoLXNvbHV0aW9ucy1wdnQtbHRkLW5hbmRuaQ=="
                    className="contact-form"
                    title="Book a Demo Class Form"
                  >
                    Loading…
                  </iframe>

                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-col">
                <div className="footer-logo">
                  <img src={footerLogo} alt="Neeraj Gaur" />
                </div>

                <p>Spreading the Joy of Fear-Free Maths Across India.</p>
              </div>

              <div className="footer-col">
                <h4>Quick Links</h4>
                <ul className="footer-links">
                  <li>
                    <a
                      href="#about"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("about");
                      }}
                    >
                      About Me
                    </a>
                  </li>
                  <li>
                    <a
                      href="#programs"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("programs");
                      }}
                    >
                      Programs
                    </a>
                  </li>
                  <li>
                    <a
                      href="#testimonials"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("testimonials");
                      }}
                    >
                      Testimonials
                    </a>
                  </li>
                  <li>
                    <a
                      href="#faq"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("faq");
                      }}
                    >
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>Legal</h4>
                <ul className="footer-links">
                  <li>
                    <a href="#about" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
                  </li>
                  <li>
                    <a href="#about" onClick={(e) => e.preventDefault()}>Terms of Service</a>
                  </li>
                </ul>

              </div>
              <div className="footer-col">
                <h4>Contact</h4>
                <p>
                  <a href="tel:+919876543210">+91 9599316407</a>
                </p>
                <p>
                  <a href="mailto:neerugaur8107@gmail.com">
                    neerugaur8107@gmail.com
                  </a>
                </p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>
                © {new Date().getFullYear()} Neeraj Gaur. All Rights Reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default App;
